import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { QRCodeService } from '../../../services/qrCodeService';

const PaymentConfirmation = ({ eventData, formData, selectedTier, quantity, total }) => {
  const [qrCode, setQrCode] = useState('');
  const [booking, setBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get booking data from form data (set by PaymentForm after successful payment)
    const bookingResult = formData?.bookingResult;
    
    if (bookingResult?.booking) {
      const bookingData = bookingResult?.booking;
      setBooking(bookingData);
      
      // Generate QR code for the booking
      if (bookingData?.qrCodeData) {
        const qrCodeURL = QRCodeService?.generateQRCodeURL(bookingData?.qrCodeData, '200x200');
        setQrCode(qrCodeURL);
      } else {
        // Create QR code data from booking information
        const qrData = {
          id: bookingData?.id,
          reference: bookingData?.reference,
          eventTitle: bookingData?.eventTitle,
          attendeeName: `${formData?.firstName} ${formData?.lastName}`,
          attendeeEmail: formData?.email,
          ticketType: bookingData?.ticketName,
          quantity: bookingData?.quantity || quantity,
          eventDate: bookingData?.eventDate,
          location: bookingData?.eventLocation,
          checkInUrl: `${window.location?.origin}/checkin/${bookingData?.reference}`,
          validationCode: bookingData?.reference
        };
        
        const qrCodeURL = QRCodeService?.generateQRCodeURL(qrData, '200x200');
        setQrCode(qrCodeURL);
      }
    } else {
      // Fallback: Generate mock data for display (should not happen in production)
      const mockBooking = {
        id: 'booking-' + Math.random()?.toString(36)?.substr(2, 9),
        reference: 'BK' + new Date()?.toISOString()?.slice(0, 10)?.replace(/-/g, '') + '-' + 
                   Math.random()?.toString(36)?.substr(2, 8)?.toUpperCase(),
        eventTitle: eventData?.title,
        eventDate: eventData?.date,
        eventLocation: eventData?.location,
        eventVenue: eventData?.venue_name,
        ticketName: selectedTier?.name,
        ticketType: selectedTier?.type,
        ticketPrice: selectedTier?.price,
        quantity: quantity,
        totalAmount: total,
        attendeeName: `${formData?.firstName} ${formData?.lastName}`,
        attendeeEmail: formData?.email,
        bookingStatus: 'confirmed',
        paymentStatus: 'succeeded',
        createdAt: new Date()?.toISOString(),
        qrCodeData: {
          reference: 'BK' + new Date()?.toISOString()?.slice(0, 10)?.replace(/-/g, '') + '-' + 
                     Math.random()?.toString(36)?.substr(2, 8)?.toUpperCase(),
          eventTitle: eventData?.title,
          attendeeName: `${formData?.firstName} ${formData?.lastName}`,
          checkInUrl: `${window.location?.origin}/checkin`
        }
      };
      
      setBooking(mockBooking);
      const qrCodeURL = QRCodeService?.generateQRCodeURL(mockBooking?.qrCodeData, '200x200');
      setQrCode(qrCodeURL);
      
      // Show a warning that this is demo mode
      console.warn('PaymentConfirmation: Using mock data - payment integration may not be complete');
    }
    
    setIsLoading(false);
  }, [eventData, formData, selectedTier, quantity, total]);

  const handleAddToWallet = async () => {
    try {
      if (booking) {
        const walletData = {
          eventTitle: booking?.eventTitle,
          eventDate: booking?.eventDate,
          eventLocation: booking?.eventLocation,
          bookingReference: booking?.reference,
          attendeeName: booking?.attendeeName,
          qrCode: qrCode
        };
        
        // In a real implementation, this would create an Apple Wallet pass
        console.log('Adding to wallet:', walletData);
        
        // Create a downloadable wallet file for now
        const walletFile = new Blob([JSON.stringify(walletData, null, 2)], {
          type: 'application/json'
        });
        
        const url = URL.createObjectURL(walletFile);
        const link = document.createElement('a');
        link.href = url;
        link.download = `wallet-pass-${booking?.reference}.json`;
        document.body?.appendChild(link);
        link?.click();
        document.body?.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error adding to wallet:', error);
      alert('Unable to add to wallet. Please try downloading the PDF instead.');
    }
  };

  const handleDownloadTicket = async () => {
    try {
      if (booking) {
        const ticketData = {
          bookingReference: booking?.reference,
          eventTitle: booking?.eventTitle,
          eventDate: booking?.eventDate,
          eventLocation: booking?.eventLocation,
          eventVenue: booking?.eventVenue,
          attendeeName: booking?.attendeeName,
          attendeeEmail: booking?.attendeeEmail,
          ticketName: booking?.ticketName,
          quantity: booking?.quantity,
          totalAmount: booking?.totalAmount,
          qrCode: qrCode,
          createdAt: booking?.createdAt || new Date()?.toISOString()
        };
        
        // Create downloadable PDF ticket data
        const ticketFile = new Blob([JSON.stringify(ticketData, null, 2)], {
          type: 'application/json'
        });
        
        const url = URL.createObjectURL(ticketFile);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ticket-${booking?.reference}.json`;
        document.body?.appendChild(link);
        link?.click();
        document.body?.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading ticket:', error);
      alert('Unable to download ticket. Please contact support.');
    }
  };

  const handleShareTicket = async () => {
    try {
      if (booking) {
        const shareData = {
          title: `${booking?.eventTitle} - My Ticket`,
          text: `I'm attending ${booking?.eventTitle}! Booking: ${booking?.reference}`,
          url: `${window.location?.origin}/booking/${booking?.reference}`
        };
        
        if (navigator.share) {
          await navigator.share(shareData);
        } else {
          // Fallback for browsers that don't support Web Share API
          await navigator.clipboard?.writeText(
            `${shareData?.text} - ${shareData?.url}`
          );
          alert('Ticket details copied to clipboard!');
        }
      }
    } catch (error) {
      console.error('Error sharing ticket:', error);
      if (error?.name !== 'AbortError') {
        alert('Unable to share ticket. Please try again.');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Processing your booking...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center">
          <div className="w-16 h-16 bg-destructive rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertTriangle" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Booking Error</h1>
          <p className="text-muted-foreground mb-6">
            Unable to process your booking. Please contact support.
          </p>
          <Button asChild>
            <Link to="/event-discovery">
              Return to Events
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} color="white" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Payment Successful!</h1>
        <p className="text-muted-foreground">
          Your tickets have been confirmed and sent to {booking?.attendeeEmail || formData?.email}
        </p>
      </div>
      
      {/* Ticket Card */}
      <div className="bg-background border border-border rounded-lg overflow-hidden mb-6">
        {/* Ticket Header */}
        <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold mb-2">{booking?.eventTitle || eventData?.title}</h2>
              <div className="space-y-1 text-sm opacity-90">
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={14} />
                  <span>{booking?.eventDate || eventData?.date}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={14} />
                  <span>{booking?.eventLocation || eventData?.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={14} />
                  <span>{eventData?.time}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm opacity-75">Booking ID</div>
              <div className="font-mono text-sm">{booking?.reference}</div>
            </div>
          </div>
        </div>

        {/* Ticket Body */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Attendee Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-foreground">Attendee Information</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Name: </span>
                  <span className="text-foreground">{booking?.attendeeName || `${formData?.firstName} ${formData?.lastName}`}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Email: </span>
                  <span className="text-foreground">{booking?.attendeeEmail || formData?.email}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Phone: </span>
                  <span className="text-foreground">{formData?.phone}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Tier: </span>
                  <span className="text-foreground">{booking?.ticketName || selectedTier?.name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Quantity: </span>
                  <span className="text-foreground">{booking?.quantity || quantity} ticket{(booking?.quantity || quantity) > 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex flex-col items-center">
              <h3 className="font-semibold text-foreground mb-4">Check-in QR Code</h3>
              <div className="w-32 h-32 border border-border rounded-lg overflow-hidden mb-3">
                {qrCode ? (
                  <Image
                    src={qrCode}
                    alt="Ticket QR Code"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-muted">
                    <Icon name="QrCode" size={48} className="text-muted-foreground" />
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Show this QR code at the event entrance
              </p>
            </div>
          </div>

          {/* Ticket Benefits */}
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="font-semibold text-foreground mb-3">Your Ticket Includes</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {selectedTier?.benefits?.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm">
                  <Icon name="Check" size={14} className="text-success" />
                  <span className="text-foreground">{benefit}</span>
                </div>
              )) || (
                <div className="flex items-center space-x-2 text-sm">
                  <Icon name="Check" size={14} className="text-success" />
                  <span className="text-foreground">Event access included</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Button
          variant="outline"
          onClick={handleAddToWallet}
          className="flex items-center justify-center space-x-2"
        >
          <Icon name="Wallet" size={16} />
          <span>Add to Wallet</span>
        </Button>
        
        <Button
          variant="outline"
          onClick={handleDownloadTicket}
          className="flex items-center justify-center space-x-2"
        >
          <Icon name="Download" size={16} />
          <span>Download PDF</span>
        </Button>
        
        <Button
          variant="outline"
          onClick={handleShareTicket}
          className="flex items-center justify-center space-x-2"
        >
          <Icon name="Share" size={16} />
          <span>Share Ticket</span>
        </Button>
      </div>
      
      {/* Order Summary */}
      <div className="bg-muted rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-foreground mb-3">Order Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Booking ID:</span>
            <span className="text-foreground font-mono">{booking?.reference}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Payment Status:</span>
            <span className="text-foreground capitalize font-semibold text-success">
              {booking?.paymentStatus || 'Succeeded'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Paid:</span>
            <span className="text-foreground font-semibold">
              ${(booking?.totalAmount || total)?.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Transaction Date:</span>
            <span className="text-foreground">
              {new Date(booking?.createdAt || Date.now())?.toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
      
      {/* Next Steps */}
      <div className="bg-background border border-border rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-foreground mb-4">What's Next?</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <Icon name="Mail" size={16} className="text-primary mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground">Check Your Email</div>
              <div className="text-xs text-muted-foreground">
                We've sent your tickets and event details to {booking?.attendeeEmail || formData?.email}
              </div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <Icon name="Calendar" size={16} className="text-primary mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground">Add to Calendar</div>
              <div className="text-xs text-muted-foreground">
                Don't forget to add the event to your calendar
              </div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <Icon name="MapPin" size={16} className="text-primary mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground">Plan Your Journey</div>
              <div className="text-xs text-muted-foreground">
                Check directions and parking information before the event
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="outline"
          asChild
          className="sm:w-auto"
        >
          <Link to="/event-discovery">
            <Icon name="Search" size={16} className="mr-2" />
            Discover More Events
          </Link>
        </Button>
        
        <Button
          variant="default"
          asChild
          className="sm:ml-auto sm:w-auto"
        >
          <Link to="/user-dashboard">
            <Icon name="Calendar" size={16} className="mr-2" />
            View My Events
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default PaymentConfirmation;